import { useMutation, useQueryClient } from '@tanstack/react-query'
import { onSuccessInvalidate, invalidateDeckDetail } from '@/hooks/invalidation'
import { useDefinedQuery } from '@/hooks/useDefinedQuery'
import type { QueryDef } from '@/hooks/useDefinedQuery'
import { withIdempotency } from '@/hooks/withIdempotency'
import { api } from '@/services/api'
import { getDecks, getDeck, getDeckStats, getDeckCollectionStatus, getSharedDeck } from '@/services/api/decks'
import type { Deck, DecksResponse, DeckResponse, DeckSummary, ShareTokenResponse } from '@/types'

const decksQueries = {
  list: {
    queryKey: () => ['decks'] as const,
    queryFn: getDecks,
    auth: 'required',
  } satisfies QueryDef<Awaited<ReturnType<typeof getDecks>>>,
  detail: {
    queryKey: (id: number) => ['deck', id] as const,
    queryFn: (id: number) => getDeck(id),
    auth: 'required',
  } satisfies QueryDef<Awaited<ReturnType<typeof getDeck>>, [number]>,
  stats: {
    queryKey: (id: number) => ['deckStats', id] as const,
    queryFn: (id: number) => getDeckStats(id),
    auth: 'required',
  } satisfies QueryDef<Awaited<ReturnType<typeof getDeckStats>>, [number]>,
  collectionStatus: {
    queryKey: (id: number) => ['deckCollectionStatus', id] as const,
    queryFn: (id: number) => getDeckCollectionStatus(id),
    auth: 'required',
  } satisfies QueryDef<Awaited<ReturnType<typeof getDeckCollectionStatus>>, [number]>,
  shared: {
    queryKey: (shareToken: string) => ['sharedDeck', shareToken] as const,
    queryFn: (shareToken: string) => getSharedDeck(shareToken),
    auth: 'public',
  } satisfies QueryDef<Awaited<ReturnType<typeof getSharedDeck>>, [string]>,
}

// Exported for invalidation.ts cross-file key reference
export { decksQueries }

export function useDecks() {
  return useDefinedQuery(decksQueries.list)
}

export function useDeck(id: number | undefined) {
  return useDefinedQuery(decksQueries.detail, id as number, { enabled: id !== undefined })
}

export function useSharedDeck(shareToken: string | undefined) {
  return useDefinedQuery(decksQueries.shared, shareToken as string, { enabled: shareToken !== undefined })
}

/** Extract a DeckSummary from a full DeckResponse for the list cache. */
function toSummary(resp: DeckResponse): DeckSummary {
  const { cards: _cards, exportText: _export, ...summary } = resp.deck
  return summary
}

/** Append a new deck summary to the decks list cache. */
function appendToList(queryClient: ReturnType<typeof useQueryClient>, resp: DeckResponse) {
  queryClient.setQueryData<DecksResponse>(decksQueries.list.queryKey(), (old) => {
    if (!old) return { decks: [toSummary(resp)] }
    return { decks: [...old.decks, toSummary(resp)] }
  })
}

/**
 * Unwraps a mutation whose `mutateAsync` resolves to a `DeckResponse` so
 * callers get the `Deck` directly, instead of reaching into `.deck` at every
 * call site. Cache writes (`onSuccess`) still run against the full response.
 */
function unwrapDeck<M extends { mutateAsync: (...args: never[]) => Promise<DeckResponse> }>(mutation: M) {
  const mutateAsync = async (...args: Parameters<M['mutateAsync']>): Promise<Deck> => {
    const resp = await mutation.mutateAsync(...args)
    return resp.deck
  }
  // Spreading a generically-typed `mutation` alongside an explicit `mutateAsync`
  // key produces an intersection of the old and new call signatures (TS can't
  // statically prove the spread's key is overridden when the source is a type
  // parameter) — the trailing `as` pins the return type to the override only.
  return { ...mutation, mutateAsync } as Omit<M, 'mutateAsync'> & { mutateAsync: typeof mutateAsync }
}

// A 422 from create/update is a validation failure (e.g. a duplicate deck
// name); the deck form surfaces it inline on the name field (see
// `useDeckBuilder`), so it opts out of the global error toast + error reporter
// via `meta.inlineErrorStatuses` (consumed by the MutationCache in `main.tsx`).
// Genuine failures (500, network) are NOT listed, so they still surface
// globally (#204).
const INLINE_VALIDATION_META = { inlineErrorStatuses: [422] }

export function useCreateDeck() {
  const queryClient = useQueryClient()
  type Vars = {
    data: {
      name: string
      description?: string
      leaderCardId: number
      isPublic?: boolean
    }
    idempotencyKey?: string
  }
  const mutation = useMutation({
    mutationFn: ({ data, idempotencyKey }: Vars) => api.createDeck(data, idempotencyKey),
    meta: INLINE_VALIDATION_META,
    onSuccess: (resp) => {
      appendToList(queryClient, resp)
    },
  })
  return unwrapDeck(withIdempotency(mutation))
}

export function useUpdateDeck() {
  const queryClient = useQueryClient()
  type Vars = {
    id: number
    data: {
      name?: string
      description?: string
      leaderCardId?: number
      isPublic?: boolean
    }
    idempotencyKey?: string
  }
  const mutation = useMutation({
    mutationFn: ({ id, data, idempotencyKey }: Vars) => api.updateDeck(id, data, idempotencyKey),
    meta: INLINE_VALIDATION_META,
    onSuccess: (resp, { id }) => {
      // Update the list cache with the new summary
      const summary = toSummary(resp)
      queryClient.setQueryData<DecksResponse>(decksQueries.list.queryKey(), (old) => {
        if (!old) return old
        return { decks: old.decks.map((d) => (d.id === id ? summary : d)) }
      })
      // Update the detail cache
      queryClient.setQueryData<DeckResponse>(decksQueries.detail.queryKey(id), resp)
    },
  })
  return withIdempotency(mutation)
}

export function useDeleteDeck() {
  const queryClient = useQueryClient()
  type Vars = { id: number; idempotencyKey?: string }
  const mutation = useMutation({
    mutationFn: ({ id, idempotencyKey }: Vars) => api.deleteDeck(id, idempotencyKey),
    ...onSuccessInvalidate(queryClient, 'decks'),
  })
  type PublicVars = number | Vars
  const normalize = (vars: PublicVars): Vars => (typeof vars === 'number' ? { id: vars } : vars)
  return withIdempotency(mutation, normalize)
}

export function useDuplicateDeck() {
  const queryClient = useQueryClient()
  type Vars = { id: number; idempotencyKey?: string }
  const mutation = useMutation({
    mutationFn: ({ id, idempotencyKey }: Vars) => api.duplicateDeck(id, idempotencyKey),
    onSuccess: (resp) => {
      appendToList(queryClient, resp)
    },
  })
  type PublicVars = number | Vars
  const normalize = (vars: PublicVars): Vars => (typeof vars === 'number' ? { id: vars } : vars)
  return unwrapDeck(withIdempotency(mutation, normalize))
}

export function useRegenerateDeckToken() {
  const queryClient = useQueryClient()
  type Vars = { id: number; idempotencyKey?: string }
  const mutation = useMutation({
    mutationFn: ({ id, idempotencyKey }: Vars) => api.regenerateDeckShareToken(id, idempotencyKey),
    onSuccess: (resp: ShareTokenResponse, { id }) => {
      // Update the detail cache with the new token
      queryClient.setQueryData<DeckResponse>(decksQueries.detail.queryKey(id), (old) => {
        if (!old) return old
        return {
          ...old,
          deck: { ...old.deck, shareToken: resp.shareToken, shareUrl: resp.shareUrl },
        }
      })
      // Update the list cache entry
      queryClient.setQueryData<DecksResponse>(decksQueries.list.queryKey(), (old) => {
        if (!old) return old
        return {
          decks: old.decks.map((d) =>
            d.id === id ? { ...d, shareToken: resp.shareToken, shareUrl: resp.shareUrl } : d,
          ),
        }
      })
    },
  })
  type PublicVars = number | Vars
  const normalize = (vars: PublicVars): Vars => (typeof vars === 'number' ? { id: vars } : vars)
  return withIdempotency(mutation, normalize)
}

export function useImportDeck() {
  const queryClient = useQueryClient()
  type Vars = { deckText: string; name?: string; idempotencyKey?: string }
  const mutation = useMutation({
    mutationFn: ({ deckText, name, idempotencyKey }: Vars) => api.importDeck(deckText, name, idempotencyKey),
    onSuccess: (resp) => {
      appendToList(queryClient, resp)
    },
  })
  return unwrapDeck(withIdempotency(mutation))
}

export function useAddCardToDeck() {
  const queryClient = useQueryClient()
  type Vars = {
    deckId: number
    cardId: number
    quantity?: number
    idempotencyKey?: string
  }
  const mutation = useMutation({
    mutationFn: ({ deckId, cardId, quantity, idempotencyKey }: Vars) =>
      api.addCardToDeck(deckId, cardId, quantity, idempotencyKey),
    onSuccess: (_data, { deckId }) => invalidateDeckDetail(queryClient, deckId),
  })
  return withIdempotency(mutation)
}

export function useUpdateDeckCard() {
  const queryClient = useQueryClient()
  type Vars = {
    deckId: number
    cardId: number
    quantity: number
    idempotencyKey?: string
  }
  const mutation = useMutation({
    mutationFn: ({ deckId, cardId, quantity, idempotencyKey }: Vars) =>
      api.updateDeckCard(deckId, cardId, quantity, idempotencyKey),
    onSuccess: (_data, { deckId }) => invalidateDeckDetail(queryClient, deckId),
  })
  return withIdempotency(mutation)
}

export function useRemoveDeckCard() {
  const queryClient = useQueryClient()
  type Vars = { deckId: number; cardId: number; idempotencyKey?: string }
  const mutation = useMutation({
    mutationFn: ({ deckId, cardId, idempotencyKey }: Vars) => api.removeDeckCard(deckId, cardId, idempotencyKey),
    onSuccess: (_data, { deckId }) => invalidateDeckDetail(queryClient, deckId),
  })
  return withIdempotency(mutation)
}
